import Head from "next/head";
import PageLayout, { siteTitle } from "@/components/PageLayout";
import { useRouter } from "next/router";
import ProductService from "@/lib/services/products.js";
import AnalyticService from "@/lib/services/analytics.js";
import { loadConfig, extractIdFromSlug, configuratorHost } from "@/lib/utils.js";
import SimpleModal from '@/components/simple_modal.js';
import Clipboard  from '@/components/clipboard';
import Image  from 'next/image'; 

import SimilarProductTile from "@/components/similar_product_tile";
import ProductNav from "@/components/product_nav"; 
import ProductTopSection from "@/components/product_top_section"; 
import ProductBottomSection from "@/components/product_bottom_section"; 
import { useEffect, useState } from 'react';
import Qs from 'qs';
function ProductPage({sku_part_query, id, product, stylesheet, favicon, page_layout, cart_enabled, editLink, 
  resource_display, configurator_settings, photometry_options, enable_asset_multiple_link_download_modal, 
  enable_asset_multiple_link_copying_modal }) {
  const primarySpecSheet = product.primary_spec_sheet?.url ? product.primary_spec_sheet : null;
  const product_photo_placement = page_layout?.product_photo_placement || "bottom";
  const product_photo_navigation = page_layout?.product_photo_navigation || "thumbnails";

  const application_photo_placement = page_layout?.application_photo_placement || "right"
  const [showSavedConfiguration, setShowSavedConfiguration ] = useState(false) ;
  const [shareConfigUrl, setShareConfigUrl] = useState('');
  //keyboard shortcut to the product edit screen
  const productShortCut = (e) => {
  if (e.key === 'L' && e.ctrlKey && e.shiftKey ) {
        window.open(atob(editLink), '_blank');
      }
  }
  useEffect(( ) => {
    document.addEventListener('keydown',  productShortCut)
    return ( ) => {
      document.removeEventListener('keydown',  productShortCut)
    }
  }, [editLink])

  const formatSku = (skus) => {
    return  Array.isArray(skus[0]) ? skus.map(a => a.join('|')).join('+') : skus.join('|');
  }


  useEffect(( ) => {
    document.addEventListener('sharedPartNumber', (e) => {
      let url = new URL(window.location); 
      let formatted = formatSku(e.detail.partNumber).replace('+','|').replace(/ /g, '');
      url.searchParams.set('sku_parts',formatted);
      url.hash = '#lexcms-configurator-section'
      setShareConfigUrl(url.toString())
      setShowSavedConfiguration(true)
      })
    }, [])

 
    return (
    <PageLayout
      page_layout={page_layout}
      stylesheet={stylesheet}
      favicon={favicon}
      cart_enabled={cart_enabled}
      meta_description={product.meta_description || product.description}
      title={product.page_title || product.name}
      >
      {product.is_discontinued &&
      <Head>
        <meta name='robots' content='noindex, nofollow'/>
      </Head>
      }
      
      <section className={'lexcms-product-page-container ' + `brand-${product.manufacturer_id}`}>

        {product.hero_image &&
        <div
          className="lexcms-product-page-hero"
          style={{ backgroundImage: `url(${product.hero_image.url})` }}
        >
          <div className="lexcms-product-page-hero-title">
            {product.name}
          </div>
        </div>
        }
        {process.env.NODE_ENV  === 'development' && 
        <div style={{marginTop: '50px'}}>
          <a href={`http://localhost:4300/products/details/${id}/edit`} target='_blank'>
            Edit Product on Lex in (Dev Only)
          </a>
        </div>
        }
        
        <ProductTopSection product={product} 
          resource_display={resource_display} 
          photometry_options={photometry_options}
          config={{enable_asset_multiple_link_copying_modal: enable_asset_multiple_link_copying_modal, 
                              enable_asset_multiple_link_download_modal: enable_asset_multiple_link_download_modal
                                 }}          primarySpecSheet={primarySpecSheet } 
        product_photo_navigation={product_photo_navigation}
        product_photo_placement={product_photo_placement} /> 
        <ProductNav product={product} /> 
          <ProductBottomSection product={product} 
                              resource_display={resource_display} 
                              sku_part_query={sku_part_query}
                              configurator_settings={configurator_settings}
                              config={{enable_asset_multiple_link_copying_modal: enable_asset_multiple_link_copying_modal, 
                              enable_asset_multiple_link_download_modal: enable_asset_multiple_link_download_modal
                                 }}
                             application_photo_placement={application_photo_placement} />
      </section>
      <SimpleModal show={showSavedConfiguration} setShow={setShowSavedConfiguration} additionalClasses={'lexcms-share-config-modal'}> 
        <h3>Share Configuration</h3> 
        <p >You can share this fully pre-configured product with others by copying the below URL </p>
        {shareConfigUrl &&
        <Clipboard pageUrl={shareConfigUrl}   />
        }
      </SimpleModal> 

    </PageLayout>
  );
}

export async function getServerSideProps({ params, req }) {
  let id = extractIdFromSlug(params)
  let token = req?.headers['lex-api-token']

  let service = await ProductService.build(token, req?.headers?.host);
  let resp, config, editLink;
  let query = (params.slug?.split('?')[1] &&  Qs.parse(params.slug?.split('?')[1])) || {}
  let sku_part_query = (query.sku_parts  || null) //nextjs sucks

  let configuratorExists = false;

  let pageLayout = loadConfig(service);
  if (pageLayout.configurator_enabled) {
    try {
      let check  = await fetch(`${configuratorHost()}/embedded_configurators/${id}?check_exists_only=true`,
        {headers: {'Authorization': `Basic ${token}`} })
      if (check.status === 200 ) { 
        configuratorExists = await check.json() 
        configuratorExists = configuratorExists.exists
      }
    }
    catch(e) {
      console.log(e)
    }
  }
  try {
    resp = await service.show(id, query?.in_preview);
    config = service.config;
    let domain =  process.env.NODE_ENV === 'production' ? 'https://www.lighting.exchange' : 'http://localhost:4300' 
    editLink =   btoa(`${domain}/products/details/${id}/edit`) ;
  } catch (e) {
    console.log(e)
    return {
      notFound: true,
    };
  }

  try {
    let analyticService = new AnalyticService(token, {}, true);
    analyticService.recordProductPageVisit(resp, req.headers);
  } catch (e) {
    console.log(e);
  }

  resp.configuratorExists = configuratorExists 


  return {
    props: { id: id, product: resp, ...pageLayout, editLink: editLink, sku_part_query: sku_part_query },
  };
}

export default ProductPage;
